import React, { useCallback, useEffect, useState } from 'react';
import { Megaphone, Send, X } from 'lucide-react';
import { useToast } from '../../contexts/ToastContext';
import * as takata from '../../services/takataService';
import { StatoPortale, TipoSegnalazione, TIPO_SEGNALAZIONE_LABEL } from '../../services/takataService';

// Segnalazione di un portale che non funziona.
//
// Stava solo nella pagina «Stato dei portali», che però si apre di rado: chi incontra il
// problema è nel registro, appena dopo una verifica andata storta, e da lì doveva sapere
// che esisteva un'altra pagina. Estratto il 29/07/2026 per averlo in entrambe.
//
// È autonomo: si carica da sé l'elenco dei marchi e dei tipi di problema, così può stare
// in qualunque pagina senza che quella debba procurarglieli. L'elenco si scarica solo
// quando il modulo viene aperto — sul registro sarebbe una richiesta in più a ogni
// caricamento, per una cosa che la maggior parte delle volte non serve.

interface Props {
  /** Chiamata a segnalazione inviata: serve a chi mostra un conteggio da aggiornare. */
  onInviata?: () => void;
}

function normalizeVin(raw: string) {
  return (raw || '').toUpperCase().replace(/[\s\-_.]/g, '');
}

const SegnalaProblema: React.FC<Props> = ({ onInviata }) => {
  const toast = useToast();
  const [aperto, setAperto] = useState(false);
  const [portali, setPortali] = useState<StatoPortale[]>([]);
  const [tipi, setTipi] = useState<TipoSegnalazione[]>([]);

  const [marca, setMarca] = useState('');
  const [tipo, setTipo] = useState<TipoSegnalazione | ''>('');
  const [descrizione, setDescrizione] = useState('');
  const [vin, setVin] = useState('');
  const [esitoAtteso, setEsitoAtteso] = useState('');
  const [esitoRegistrato, setEsitoRegistrato] = useState('');
  const [saving, setSaving] = useState(false);

  const caricaElenchi = useCallback(async () => {
    if (portali.length) return;
    try {
      const stato = await takata.getStatoPortali();
      setPortali(Array.isArray(stato.portali) ? stato.portali : []);
      setTipi(Array.isArray(stato.tipiSegnalazione) ? stato.tipiSegnalazione : []);
    } catch (e) {
      toast.error('Non riesco a caricare l\'elenco dei marchi');
    }
  }, [portali.length, toast]);

  useEffect(() => { if (aperto) caricaElenchi(); }, [aperto, caricaElenchi]);

  const vinNorm = normalizeVin(vin);
  // Telaio OBBLIGATORIO (30/07/2026): senza, la segnalazione non si può riprodurre — e
  // riprodurla è l'unico modo di correggere qualcosa.
  const vinOk = vinNorm.length === 17 && !/[IOQ]/.test(vinNorm);

  const invia = async () => {
    if (!marca) { toast.error('Scegli la marca'); return; }
    if (!tipo) { toast.error('Scegli il tipo di problema'); return; }
    if (!vinOk) { toast.error('Serve il telaio su cui è successo (17 caratteri, senza I O Q)'); return; }
    setSaving(true);
    try {
      await takata.inviaSegnalazione({
        marca,
        tipo,
        descrizione,
        vin: vinNorm,
        esitoAtteso: tipo === 'esito_sbagliato' ? esitoAtteso : undefined,
        esitoRegistrato: tipo === 'esito_sbagliato' ? esitoRegistrato : undefined,
      });
      toast.success('Segnalazione inviata, grazie!');
      setDescrizione(''); setVin(''); setEsitoAtteso(''); setEsitoRegistrato(''); setTipo(''); setMarca('');
      setAperto(false);
      if (onInviata) onInviata();
    } catch (e: any) {
      toast.error(e?.response?.data?.message || 'Errore nell\'invio della segnalazione');
    } finally {
      setSaving(false);
    }
  };

  if (!aperto) {
    return (
      <button
        onClick={() => setAperto(true)}
        className="w-full flex items-center justify-center gap-2 py-2.5 rounded-xl border border-dashed border-gray-300 dark:border-gray-600 text-sm font-medium text-gray-600 dark:text-gray-400 hover:border-red-400 hover:text-red-600 dark:hover:text-red-400 transition-colors"
      >
        <Megaphone className="w-4 h-4" /> Un portale ti ha dato problemi? Segnalalo
      </button>
    );
  }

  return (
    <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-5">
      <div className="flex items-center gap-2 mb-1">
        <Megaphone className="w-4 h-4 text-red-600 dark:text-red-400 flex-shrink-0" />
        <h2 className="text-sm font-bold text-gray-900 dark:text-white">Segnala un malfunzionamento</h2>
        <button onClick={() => setAperto(false)} className="ml-auto text-gray-400 hover:text-gray-700 dark:hover:text-gray-200" aria-label="Chiudi">
          <X className="w-4 h-4" />
        </button>
      </div>
      <p className="text-xs text-gray-500 dark:text-gray-400 mb-3">
        Le verifiche non riuscite non lasciano traccia da nessuna parte: se non ce lo dici tu,
        non possiamo saperlo.
      </p>

      <div className="grid sm:grid-cols-2 gap-3">
        <div>
          <label className="text-xs font-semibold text-gray-700 dark:text-gray-300">Marca</label>
          {/* Si invia la CHIAVE minuscola, la forma con cui la marca viene salvata e
              filtrata: mandare l'etichetta ("RAM") renderebbe la segnalazione invisibile
              ai filtri amministrativi. */}
          <select value={marca} onChange={(e) => setMarca(e.target.value)}
            className="mt-1 w-full px-3 py-2 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-900 text-gray-900 dark:text-white text-sm">
            <option value="">Seleziona…</option>
            {portali.map((p) => <option key={p.chiave} value={p.chiave}>{p.marca}</option>)}
          </select>
        </div>
        <div>
          <label className="text-xs font-semibold text-gray-700 dark:text-gray-300">Tipo di problema</label>
          <select value={tipo} onChange={(e) => setTipo(e.target.value as TipoSegnalazione)}
            className="mt-1 w-full px-3 py-2 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-900 text-gray-900 dark:text-white text-sm">
            <option value="">Seleziona…</option>
            {tipi.map((t) => <option key={t} value={t}>{TIPO_SEGNALAZIONE_LABEL[t] || t}</option>)}
          </select>
        </div>
      </div>

      {tipo === 'esito_sbagliato' && (
        <div className="grid sm:grid-cols-2 gap-3 mt-3 p-3 rounded-lg bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800">
          <div className="sm:col-span-2 text-xs text-red-700 dark:text-red-300">
            Questo caso è il più grave: potrebbe esserci un certificato già emesso con l'esito
            sbagliato. Indica cosa mostrava il portale e cosa è stato registrato.
          </div>
          <div>
            <label className="text-xs font-semibold text-gray-700 dark:text-gray-300">Esito mostrato dal portale</label>
            <input value={esitoAtteso} onChange={(e) => setEsitoAtteso(e.target.value)} maxLength={100}
              className="mt-1 w-full px-3 py-2 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-900 text-gray-900 dark:text-white text-sm" />
          </div>
          <div>
            <label className="text-xs font-semibold text-gray-700 dark:text-gray-300">Esito registrato da noi</label>
            <input value={esitoRegistrato} onChange={(e) => setEsitoRegistrato(e.target.value)} maxLength={100}
              className="mt-1 w-full px-3 py-2 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-900 text-gray-900 dark:text-white text-sm" />
          </div>
        </div>
      )}

      <div className="mt-3">
        <label className="text-xs font-semibold text-gray-700 dark:text-gray-300">Descrizione</label>
        <textarea value={descrizione} onChange={(e) => setDescrizione(e.target.value)} rows={3} maxLength={2000}
          placeholder="Cosa è successo, in poche righe"
          className="mt-1 w-full px-3 py-2 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-900 text-gray-900 dark:text-white text-sm" />
      </div>

      <div className="mt-3">
        <label className="text-xs font-semibold text-gray-700 dark:text-gray-300">Telaio su cui è successo <span className="text-red-500">*</span></label>
        <input value={vin} onChange={(e) => setVin(e.target.value)} maxLength={25} placeholder="17 caratteri"
          className="mt-1 w-full px-3 py-2 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-900 text-gray-900 dark:text-white text-sm uppercase" />
        {vinNorm.length > 0 && (
          <span className={`text-2xs ${vinOk ? 'text-green-600' : 'text-red-500'}`}>{vinNorm.length}/17</span>
        )}
        <p className="text-2xs text-gray-500 dark:text-gray-400 mt-1">
          Senza il telaio non possiamo riprodurre il problema, quindi nemmeno correggerlo.
        </p>
      </div>

      <button onClick={invia} disabled={saving || !marca || !tipo || !vinOk}
        className="mt-4 w-full py-2.5 rounded-lg bg-gradient-to-r from-red-500 to-orange-600 text-white font-semibold disabled:opacity-50 flex items-center justify-center gap-2">
        <Send className="w-4 h-4" /> {saving ? 'Invio…' : 'Invia segnalazione'}
      </button>
    </div>
  );
};

export default SegnalaProblema;
